// React 18 createRoot helper for the Backbone↔React bridge.
//
// React 18 replaces ReactDOM.render(el, node) / unmountComponentAtNode(node)
// with a persistent root object: createRoot(node) once, then root.render(el)
// and root.unmount(). Backbone views mount React into a container and tear it
// down on remove(), and some containers re-render multiple times — so we must
// create the root ONCE per DOM node and reuse it (calling createRoot twice on
// the same node warns and leaks). A WeakMap keyed by the container element
// keeps one root per node and lets unmountReact() find it by element, even when
// the render and the unmount happen in different modules.
import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

const rootMap = new WeakMap();

export const getOrCreateRoot = el => {
    if (!el) {
        return null;
    }
    let root = rootMap.get(el);
    if (!root) {
        root = createRoot(el);
        rootMap.set(el, root);
    }
    return root;
};

export const renderReact = (el, element) => {
    const root = getOrCreateRoot(el);
    if (root) {
        // App-wide StrictMode: every React root in the app mounts through here,
        // so wrapping once enables StrictMode everywhere. No-op in production.
        // STRICT_MODE (DefinePlugin, default true) lets a dev opt out via .env;
        // the typeof guard keeps this working in jest, where it isn't defined.
        const strictModeOff = typeof STRICT_MODE !== 'undefined' && STRICT_MODE === false;
        root.render(strictModeOff ? element : <StrictMode>{element}</StrictMode>);
    }
    return root;
};

export const unmountReact = el => {
    const root = el && rootMap.get(el);
    if (root) {
        root.unmount();
        rootMap.delete(el);
    }
};
